/**
 * ProtectedRoute — wraps any route that requires an active session.
 * Unauthenticated visitors are shown the AdminLogin gate instead.
 */
import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import AdminLogin from "@/pages/AdminLogin";

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  useEffect(() => {
    // Set up listener BEFORE calling getSession to avoid race conditions
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Still resolving — show nothing to avoid flash
  if (session === undefined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <span className="font-sans text-[9px] uppercase tracking-[0.25em] text-muted-foreground/40 animate-pulse">
          Authenticating…
        </span>
      </div>
    );
  }

  if (!session) return <AdminLogin />;

  return <>{children}</>;
}
